import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { Loader } from "lucide-react";
import { useApplyGroup } from "../hooks/useCountrySettings";

// One group switch (registration for app submission, or crypto). The two panels
// never change each other's setting. A button first shows exactly which
// countries it will change; nothing is written until that is confirmed.
function GroupPanel({ group, title, description, restrictLabel, restoreLabel, status }) {
  const { mutateAsync, isPending } = useApplyGroup();
  const [plan, setPlan] = useState(null); // preview result awaiting confirmation

  const open = async (action) => {
    try {
      const result = await mutateAsync({ group, action, preview: true });
      if (result.willChange === 0) {
        toast.success("Nothing to change: every country on this list is already set.");
        return;
      }
      setPlan({ ...result, action });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not load the preview");
    }
  };

  const confirm = async () => {
    try {
      const result = await mutateAsync({ group, action: plan.action, preview: false });
      toast.success(`${result.willChange} countries updated`);
      setPlan(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not apply the change");
    }
  };

  const restrictPending = status?.restrictPending;
  const restorePending = status?.restorePending;

  return (
    <div className="flex-1 p-4 rounded border border-gray-200 min-w-[300px]">
      <h2 className="text-base font-bold text-gray-900">{title}</h2>
      <p className="mt-1 text-xs text-gray-500">{description}</p>

      <div className="flex flex-wrap gap-2 mt-4">
        <button
          type="button"
          disabled={isPending}
          onClick={() => open("restrict")}
          className="px-4 py-2 text-sm font-bold text-white bg-[#013941] rounded-3xl transition-all hover:bg-[#012126] disabled:opacity-50"
        >
          {restrictLabel}
          {restrictPending != null && <span className="ml-2 text-xs font-medium opacity-70">({restrictPending} to change)</span>}
        </button>
        <button
          type="button"
          disabled={isPending}
          onClick={() => open("restore")}
          className="px-4 py-2 text-sm font-bold text-[#013941] rounded-3xl border border-[#013941] transition-all hover:bg-gray-50 disabled:opacity-50"
        >
          {restoreLabel}
          {restorePending != null && <span className="ml-2 text-xs font-medium opacity-70">({restorePending} to change)</span>}
        </button>
      </div>

      {plan && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/40" role="dialog" aria-modal="true">
          <div className="flex flex-col w-full max-w-lg max-h-[80vh] bg-white rounded-xl shadow-xl">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">
                {plan.action === "restrict" ? restrictLabel : restoreLabel}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                This will switch <strong>{plan.field === "isSupported" ? "registration" : "crypto"}</strong>{" "}
                <strong>{plan.to ? "on" : "off"}</strong> for {plan.willChange} {plan.willChange === 1 ? "country" : "countries"}
                {plan.unchanged > 0 ? ` (${plan.unchanged} on this list already are)` : ""}. Existing users keep their accounts.
              </p>
            </div>
            <ul className="overflow-y-auto flex-1 px-5 py-3 text-sm text-gray-700 columns-2">
              {plan.countries.map((c) => (
                <li key={c.countryCode || c.country}>{c.country}</li>
              ))}
            </ul>
            <div className="flex gap-2 justify-end p-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setPlan(null)}
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-3xl border border-gray-200 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={isPending}
                className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-white bg-[#013941] rounded-3xl hover:bg-[#012126] disabled:opacity-50"
              >
                {isPending && <Loader className="w-4 h-4 animate-spin" />}
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupPanel;
